import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import FadeIn from "../ui/FadeIn";
import styles from "./JobListings.module.css";

const FILTERS = [
  { label: "All Opportunities", value: "" },
  { label: "Pay While Working", value: "Pay While Working - Europe" },
  { label: "Gulf Jobs",         value: "Gulf Job Opportunities" },
  { label: "Canada Visa",       value: "Canada Visa Application" },
];

const SERVICE_COLORS = {
  "Pay While Working - Europe": { bg: "#fff8eb", text: "#b45309", dot: "#d4a847" },
  "Gulf Job Opportunities":     { bg: "#fff1f2", text: "#be123c", dot: "#c0392b" },
  "Canada Visa Application":    { bg: "#eff6ff", text: "#1d4ed8", dot: "#3b82f6" },
};

export default function JobListings() {
  const [jobs,      setJobs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState("");
  const [expanded,  setExpanded]  = useState(null); // job id with expanded details
  const [lightbox,  setLightbox]  = useState(null); // job with poster open

  useEffect(() => { fetchJobs(); }, []);

  async function fetchJobs() {
    setLoading(true);
    const { data, error } = await supabase
      .from("jobs")
      .select("*")
      .eq("is_active", true)
      .order("created_at", { ascending: false });

    if (!error) setJobs(data ?? []);
    setLoading(false);
  }

  const filtered = filter
    ? jobs.filter((j) => j.service_type === filter)
    : jobs;

  const toggleExpand = (id) =>
    setExpanded((prev) => (prev === id ? null : id));

  const daysLeft = (deadline) => {
    if (!deadline) return null;
    const diff = Math.ceil(
      (new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24)
    );
    return diff;
  };

  return (
    <section id="careers-abroad" className={styles.section}>
      <div className="section-wrap">
        {/* Header */}
        <FadeIn>
          <div className={styles.header}>
            <div className="sec-label">Now Hiring</div>
            <h2 className="sec-title">
              Current Job<br />Opportunities
            </h2>
            <p className={styles.headerSub}>
              Live openings updated directly by our team. Apply through our
              office — Nanak House, Kimathi Street, 4th Floor, Room 407.
            </p>
          </div>
        </FadeIn>

        {/* Filter tabs */}
        <FadeIn delay={0.1}>
          <div className={styles.filters}>
            {FILTERS.map((f) => (
              <button
                key={f.value}
                className={`${styles.filterBtn} ${filter === f.value ? styles.active : ""}`}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
                {f.value && (
                  <span className={styles.filterCount}>
                    {jobs.filter((j) => j.service_type === f.value).length}
                  </span>
                )}
                {!f.value && (
                  <span className={styles.filterCount}>{jobs.length}</span>
                )}
              </button>
            ))}
          </div>
        </FadeIn>

        {/* Content */}
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner} />
            <span>Loading opportunities…</span>
          </div>
        ) : filtered.length === 0 ? (
          <FadeIn>
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>📋</div>
              <h3>No openings at the moment</h3>
              <p>Check back soon or contact our office directly.</p>
            </div>
          </FadeIn>
        ) : (
          <div className={styles.grid}>
            {filtered.map((job, i) => {
              const colors  = SERVICE_COLORS[job.service_type] ?? { bg: "#f5f4f0", text: "#374151", dot: "#6b7280" };
              const days    = daysLeft(job.deadline);
              const urgent  = days !== null && days <= 7 && days >= 0;
              const expired = days !== null && days < 0;
              const isOpen  = expanded === job.id;

              return (
                <FadeIn key={job.id} delay={i * 0.07}>
                  <div className={`${styles.card} ${isOpen ? styles.cardOpen : ""}`}>

                    {/* Poster */}
                    {job.poster_url && (
                      <div
                        className={styles.posterWrap}
                        onClick={() => setLightbox(job)}
                      >
                        <img
                          src={job.poster_url}
                          alt={`${job.title} poster`}
                          className={styles.posterImg}
                        />
                        <div className={styles.posterOverlay}>
                          <span className={styles.viewPoster}>🔍 View Full Poster</span>
                        </div>
                      </div>
                    )}

                    <div className={styles.cardBody}>
                      {/* Service tag */}
                      <div
                        className={styles.serviceTag}
                        style={{ background: colors.bg, color: colors.text }}
                      >
                        <span
                          className={styles.dot}
                          style={{ background: colors.dot }}
                        />
                        {job.service_type}
                      </div>

                      {/* Title + location */}
                      <h3 className={styles.jobTitle}>{job.title}</h3>
                      {job.country && (
                        <div className={styles.location}>
                          <span>📍</span> {job.country}
                        </div>
                      )}

                      {/* Deadline badge */}
                      {job.deadline && (
                        <div className={`${styles.deadline} ${urgent ? styles.urgent : ""} ${expired ? styles.expired : ""}`}>
                          {expired
                            ? "⚠ Deadline passed"
                            : urgent
                            ? `🔥 Closing in ${days} day${days === 1 ? "" : "s"}`
                            : `📅 Deadline: ${new Date(job.deadline).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" })}`
                          }
                        </div>
                      )}

                      {/* Expandable details */}
                      {(job.description || job.requirements) && (
                        <>
                          <button
                            className={styles.expandBtn}
                            onClick={() => toggleExpand(job.id)}
                          >
                            {isOpen ? "Hide details ↑" : "View details ↓"}
                          </button>

                          {isOpen && (
                            <div className={styles.details}>
                              {job.description && (
                                <div className={styles.detailBlock}>
                                  <div className={styles.detailLabel}>Overview</div>
                                  <p>{job.description}</p>
                                </div>
                              )}
                              {job.requirements && (
                                <div className={styles.detailBlock}>
                                  <div className={styles.detailLabel}>Requirements</div>
                                  <p>{job.requirements}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* CTA */}
                      <a
                        href="#contact"
                        className={styles.applyBtn}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        Apply Now →
                      </a>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        )}

        {/* Office CTA banner */}
        <FadeIn delay={0.1}>
          <div className={styles.ctaBanner}>
            <div className={styles.ctaLeft}>
              <div className={styles.ctaTitle}>Don't see what you're looking for?</div>
              <div className={styles.ctaSub}>
                Visit our office or send us a message — new opportunities are added regularly.
              </div>
            </div>
            <button
              className={styles.ctaBtn}
              onClick={() =>
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Contact Us
            </button>
          </div>
        </FadeIn>
      </div>

      {/* Poster lightbox */}
      {lightbox && (
        <div className={styles.lightbox} onClick={() => setLightbox(null)}>
          <div
            className={styles.lightboxInner}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.lightboxClose}
              onClick={() => setLightbox(null)}
            >
              ✕
            </button>
            <img
              src={lightbox.poster_url}
              alt={lightbox.title}
              className={styles.lightboxImg}
            />
            <div className={styles.lightboxCaption}>
              <strong>{lightbox.title}</strong>
              {lightbox.country && <span> · 📍 {lightbox.country}</span>}
              {lightbox.deadline && (
                <span>
                  {" "}· Deadline:{" "}
                  {new Date(lightbox.deadline).toLocaleDateString("en-KE", {
                    day: "numeric", month: "long", year: "numeric",
                  })}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
